import { Component, HostListener, inject, signal, computed, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, Validators } from '@angular/forms';
import { UserService, User, UserRole, GetUsersParams } from '../../core/services/user';
import { AuthService } from '../../core/services/auth';

const ALL_ROLES: UserRole[] = ['super_admin', 'admin', 'content_creator'];
type TeamMenu = 'role' | 'status' | 'sort' | 'order' | 'pageSize';
const SORT_FIELDS: { value: GetUsersParams['sort_by']; label: string }[] = [
  { value: 'created_at', label: 'Date created' },
  { value: 'first_name', label: 'First name' },
  { value: 'last_name', label: 'Last name' },
  { value: 'email', label: 'Email' },
];

@Component({
  selector: 'app-my-team',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule],
  templateUrl: './my-team.html',
  styleUrl: './my-team.css'
})
export class MyTeamComponent implements OnInit, OnDestroy {
  private userService = inject(UserService);
  private auth = inject(AuthService);
  private fb = inject(FormBuilder);

  // ─── State ──────────────────────────────────────────────────────────────────
  users = signal<User[]>([]);
  total = signal(0);
  page = signal(1);
  pageSize = signal(20);
  totalPages = signal(0);
  hasNext = signal(false);
  hasPrev = signal(false);

  isLoading = signal(false);
  errorMsg = signal<string | null>(null);
  searchQuery = signal('');
  private searchDebounceTimer: ReturnType<typeof setTimeout> | null = null;
  roleFilter = signal<UserRole | ''>('');
  isActiveFilter = signal<boolean | ''>('');
  sortBy = signal<GetUsersParams['sort_by']>('created_at');
  sortOrder = signal<'asc' | 'desc'>('desc');
  readonly filterMenus: Record<TeamMenu, { open: ReturnType<typeof signal<boolean>>; closing: ReturnType<typeof signal<boolean>>; timer: ReturnType<typeof setTimeout> | null }> = {
    role: { open: signal(false), closing: signal(false), timer: null },
    status: { open: signal(false), closing: signal(false), timer: null },
    sort: { open: signal(false), closing: signal(false), timer: null },
    order: { open: signal(false), closing: signal(false), timer: null },
    pageSize: { open: signal(false), closing: signal(false), timer: null },
  };
  menuClosing = signal(false);
  private menuCloseTimer: ReturnType<typeof setTimeout> | null = null;

  readonly sortFields = SORT_FIELDS;
  readonly roleOptions: { value: '' | UserRole; label: string }[] = [
    { value: '', label: 'All roles' },
    { value: 'content_creator', label: 'Content Editor' },
    { value: 'admin', label: 'Admin' },
    { value: 'super_admin', label: 'Super Admin' },
  ];
  readonly isActiveOptions: { value: '' | boolean; label: string }[] = [
    { value: '', label: 'All' },
    { value: true, label: 'Active' },
    { value: false, label: 'Inactive' },
  ];

  // Modals
  showInviteModal = signal(false);
  inviteClosing = signal(false);
  private inviteCloseTimer: ReturnType<typeof setTimeout> | null = null;
  showRoleModal = signal(false);
  showConfirmModal = signal(false);
  selectedUser = signal<User | null>(null);
  confirmAction = signal<'activate' | 'deactivate' | null>(null);

  // Form states
  isSaving = signal(false);
  saveError = signal<string | null>(null);
  saveSuccess = signal<string | null>(null);

  // Role editing (single role: content_creator | admin | super_admin)
  editingRoles = signal<string[]>(['content_creator']);

  /** Set the single role being edited (enforces one role only). */
  setEditingRole(role: UserRole): void {
    if (role === 'super_admin' && this.hasExistingSuperAdmin()) return;
    this.editingRoles.set([role]);
  }

  /** Roles offered in the Edit Role modal (single choice). */
  readonly rolesForEdit: UserRole[] = ['content_creator', 'admin', 'super_admin'];

  /** Current single role in the edit modal (for template). */
  editingRole(): UserRole {
    const r = this.editingRoles()[0];
    return (r === 'super_admin' || r === 'admin' || r === 'content_creator') ? r : 'content_creator';
  }

  // ─── Computed ────────────────────────────────────────────────────────────────
  totalCount = computed(() => this.total());
  activeCount = computed(() => this.users().filter(u => u.is_active).length);
  adminCount = computed(() => this.users().filter(u => u.roles.includes('admin') || u.roles.includes('super_admin')).length);

  /** True if there is already a user with super_admin (only one allowed) */
  hasExistingSuperAdmin = computed(() => this.users().some(u => u.roles.includes('super_admin')));

  /** Super admin cannot have role changed or be deactivated by anyone */
  isSuperAdmin(user: User): boolean {
    return user.roles.includes('super_admin');
  }

  // ─── Invite Form ─────────────────────────────────────────────────────────────
  inviteForm = this.fb.group({
    first_name: ['', [Validators.required, Validators.minLength(1)]],
    last_name:  ['', [Validators.required, Validators.minLength(1)]],
    email:      ['', [Validators.required, Validators.email]],
    role:       ['content_creator' as UserRole],
  });

  /** Content editor cannot see other users; only super_admin and admin can. */
  canSeeOtherUsers(): boolean {
    const roles = this.auth.currentUser()?.roles as string[] | undefined;
    return !!roles && (roles.includes('super_admin') || roles.includes('admin'));
  }

  // ─── Lifecycle ───────────────────────────────────────────────────────────────
  ngOnInit(): void {
    if (this.canSeeOtherUsers()) this.loadUsers();
  }

  ngOnDestroy(): void {
    if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer);
    if (this.menuCloseTimer) clearTimeout(this.menuCloseTimer);
    if (this.inviteCloseTimer) clearTimeout(this.inviteCloseTimer);
    for (const menu of Object.values(this.filterMenus)) {
      if (menu.timer) clearTimeout(menu.timer);
    }
  }

  @HostListener('document:click')
  closeFiltersFromOutside(): void {
    this.closeFilterMenus();
  }

  @HostListener('document:keydown.escape')
  closeMenusFromEscape(): void {
    this.closeFilterMenus();
    this.closeMenus();
  }

  loadUsers(): void {
    this.isLoading.set(true);
    this.errorMsg.set(null);
    const params: GetUsersParams = {
      page: this.page(),
      page_size: this.pageSize(),
      sort_by: this.sortBy(),
      sort_order: this.sortOrder(),
    };
    const search = this.searchQuery().trim();
    if (search) params.search = search;
    const role = this.roleFilter();
    if (role) params.role = role;
    const isActive = this.isActiveFilter();
    if (isActive !== '') params.is_active = isActive;

    this.userService.getUsers(params).subscribe({
      next: (res) => {
        this.users.set(res.items);
        this.total.set(res.total);
        this.page.set(res.page);
        this.pageSize.set(res.page_size);
        this.totalPages.set(res.total_pages);
        this.hasNext.set(res.has_next);
        this.hasPrev.set(res.has_prev);
        this.isLoading.set(false);
      },
      error: (err) => {
        this.errorMsg.set(err?.error?.detail ?? 'Could not load users.');
        this.isLoading.set(false);
      }
    });
  }

  roleFilterLabel(): string {
    return this.roleOptions.find(opt => opt.value === this.roleFilter())?.label ?? 'All roles';
  }

  statusFilterLabel(): string {
    return this.isActiveOptions.find(opt => opt.value === this.isActiveFilter())?.label ?? 'All';
  }

  sortFilterLabel(): string {
    return this.sortFields.find(opt => opt.value === this.sortBy())?.label ?? 'Date created';
  }

  orderFilterLabel(): string {
    return this.sortOrder() === 'asc' ? 'Oldest first' : 'Newest first';
  }

  pageSizeLabel(): string {
    return `${this.pageSize()} per page`;
  }

  toggleFilterMenu(id: TeamMenu): void {
    const menu = this.filterMenus[id];
    if (menu.open()) {
      this.closeFilterMenu(id);
      return;
    }
    (Object.keys(this.filterMenus) as TeamMenu[])
      .filter(other => other !== id)
      .forEach(other => this.closeFilterMenu(other));
    this.closeMenus();
    if (menu.timer) clearTimeout(menu.timer);
    menu.timer = null;
    menu.closing.set(false);
    menu.open.set(true);
  }

  chooseRoleFilter(role: '' | UserRole): void {
    if (role !== this.roleFilter()) {
      this.roleFilter.set(role);
      this.applyFilters();
    }
    this.closeFilterMenu('role');
  }

  chooseStatusFilter(value: '' | boolean): void {
    if (value !== this.isActiveFilter()) {
      this.isActiveFilter.set(value);
      this.applyFilters();
    }
    this.closeFilterMenu('status');
  }

  chooseSortFilter(field: GetUsersParams['sort_by']): void {
    if (field !== this.sortBy()) this.setSort(field);
    this.closeFilterMenu('sort');
  }

  chooseOrderFilter(order: 'asc' | 'desc'): void {
    if (order !== this.sortOrder()) {
      this.sortOrder.set(order);
      this.page.set(1);
      this.loadUsers();
    }
    this.closeFilterMenu('order');
  }

  choosePageSize(size: number): void {
    if (size !== this.pageSize()) this.setPageSize(size);
    this.closeFilterMenu('pageSize');
  }

  closeFilterMenus(): void {
    (Object.keys(this.filterMenus) as TeamMenu[]).forEach(id => this.closeFilterMenu(id));
  }

  private closeFilterMenu(id: TeamMenu): void {
    const menu = this.filterMenus[id];
    if (!menu.open() || menu.closing()) return;
    menu.closing.set(true);
    if (menu.timer) clearTimeout(menu.timer);
    menu.timer = setTimeout(() => {
      menu.open.set(false);
      menu.closing.set(false);
      menu.timer = null;
    }, 200);
  }

  applyFilters(): void {
    this.page.set(1);
    this.loadUsers();
  }

  onSearchInput(value: string): void {
    this.searchQuery.set(value);
    if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer);
    this.searchDebounceTimer = setTimeout(() => this.applyFilters(), 300);
  }

  goToPage(p: number): void {
    if (p < 1 || p > this.totalPages()) return;
    this.page.set(p);
    this.loadUsers();
  }

  setPageSize(size: number): void {
    this.pageSize.set(size);
    this.page.set(1);
    this.loadUsers();
  }

  setSort(field: GetUsersParams['sort_by'], order?: 'asc' | 'desc'): void {
    this.sortBy.set(field);
    if (order) this.sortOrder.set(order);
    this.page.set(1);
    this.loadUsers();
  }

  // ─── Invite ──────────────────────────────────────────────────────────────────
  openInviteModal(): void {
    if (this.inviteCloseTimer) {
      clearTimeout(this.inviteCloseTimer);
      this.inviteCloseTimer = null;
    }
    this.inviteClosing.set(false);
    this.inviteForm.reset({ role: 'content_creator' });
    this.saveError.set(null);
    this.saveSuccess.set(null);
    this.showInviteModal.set(true);
  }

  closeInviteModal(): void {
    if (!this.showInviteModal() || this.inviteClosing()) return;
    this.inviteClosing.set(true);
    this.inviteCloseTimer = setTimeout(() => {
      this.showInviteModal.set(false);
      this.inviteClosing.set(false);
      this.inviteCloseTimer = null;
    }, 200);
  }

  submitInvite(): void {
    if (this.inviteForm.invalid) { this.inviteForm.markAllAsTouched(); return; }
    this.isSaving.set(true);
    this.saveError.set(null);
    const v = this.inviteForm.value;
    const payload = {
      first_name: v.first_name!,
      last_name:  v.last_name!,
      email:      v.email!,
      roles:      [v.role as UserRole]
    };
    this.userService.inviteUser(payload).subscribe({
      next: (res) => {
        this.isSaving.set(false);
        this.saveSuccess.set(res.message ?? `${v.first_name} ${v.last_name} was invited successfully!`);
        setTimeout(() => { this.closeInviteModal(); this.loadUsers(); }, 1200);
      },
      error: (err) => {
        this.isSaving.set(false);
        this.saveError.set(err?.error?.detail ?? 'Could not send invitation.');
      }
    });
  }

  // ─── Role Edit ───────────────────────────────────────────────────────────────
  openRoleModal(user: User): void {
    if (this.isSuperAdmin(user)) return;
    this.selectedUser.set(user);
    const first = user.roles.find(r => ALL_ROLES.includes(r as UserRole));
    this.editingRoles.set(first ? [first] : ['content_creator']);
    this.saveError.set(null);
    this.saveSuccess.set(null);
    this.showRoleModal.set(true);
  }

  closeRoleModal(): void { this.showRoleModal.set(false); this.selectedUser.set(null); }

  /** Super admin option disabled when another user already has it (only one allowed) */
  isRoleDisabled(role: string): boolean {
    return role === 'super_admin' && this.hasExistingSuperAdmin();
  }

  saveRoles(): void {
    const user = this.selectedUser();
    if (!user) return;
    this.isSaving.set(true);
    this.saveError.set(null);
    this.userService.updateUserRoles(user.id, this.editingRoles()).subscribe({
      next: (updated) => {
        this.users.update(list => list.map(u => u.id === updated.id ? updated : u));
        this.isSaving.set(false);
        this.saveSuccess.set('Roles updated!');
        setTimeout(() => { this.closeRoleModal(); this.loadUsers(); }, 900);
      },
      error: (err) => {
        this.isSaving.set(false);
        this.saveError.set(err?.error?.detail ?? 'Could not update roles.');
      }
    });
  }

  // ─── Activate / Deactivate ───────────────────────────────────────────────────
  openConfirmModal(user: User, action: 'activate' | 'deactivate'): void {
    if (action === 'deactivate' && this.isSuperAdmin(user)) return;
    this.selectedUser.set(user);
    this.confirmAction.set(action);
    this.saveError.set(null);
    this.showConfirmModal.set(true);
  }

  closeConfirmModal(): void { this.showConfirmModal.set(false); this.selectedUser.set(null); }

  confirmToggleActivation(): void {
    const user = this.selectedUser();
    const action = this.confirmAction();
    if (!user || !action) return;
    this.isSaving.set(true);
    const obs = action === 'activate'
      ? this.userService.activateUser(user.id)
      : this.userService.deactivateUser(user.id);

    obs.subscribe({
      next: (updated) => {
        this.users.update(list => list.map(u => u.id === updated.id ? updated : u));
        this.isSaving.set(false);
        this.closeConfirmModal();
        this.loadUsers();
      },
      error: (err) => {
        this.isSaving.set(false);
        this.saveError.set(err?.error?.detail ?? 'Operation could not be completed.');
      }
    });
  }

  // ─── Helpers ─────────────────────────────────────────────────────────────────
  getInitials(user: User): string {
    return `${user.first_name?.[0] ?? ''}${user.last_name?.[0] ?? ''}`.toUpperCase();
  }

  getRoleLabel(role: string): string {
    const map: Record<string, string> = {
      super_admin:     'Super Admin',
      admin:           'Admin',
      content_creator: 'Editor',
    };
    return map[role] ?? role;
  }

  getRoleBadgeClass(role: string): string {
    const map: Record<string, string> = {
      super_admin:     'role-badge--super',
      admin:           'role-badge--admin',
      content_creator: 'role-badge--editor',
    };
    return `role-badge ${map[role] ?? ''}`;
  }

  /** Used to open/close per-row dropdown */
  activeMenuId = signal<string | null>(null);

  toggleMenu(userId: string, event: Event): void {
    event.stopPropagation();
    this.closeFilterMenus();
    if (this.activeMenuId() === userId && !this.menuClosing()) {
      this.closeMenus();
      return;
    }
    if (this.menuCloseTimer) {
      clearTimeout(this.menuCloseTimer);
      this.menuCloseTimer = null;
    }
    this.menuClosing.set(false);
    this.activeMenuId.set(userId);
  }

  closeMenus(): void {
    if (!this.activeMenuId() || this.menuClosing()) return;
    this.menuClosing.set(true);
    this.menuCloseTimer = setTimeout(() => {
      this.activeMenuId.set(null);
      this.menuClosing.set(false);
      this.menuCloseTimer = null;
    }, 200);
  }

  /** Pagination display: end index of current page (e.g. "Showing 1–20 of 47") */
  pageRangeEnd(): number {
    return Math.min(this.page() * this.pageSize(), this.total());
  }

  pageRangeStart(): number {
    return this.total() === 0 ? 0 : (this.page() - 1) * this.pageSize() + 1;
  }
}
